import React, { useEffect, useRef, useState } from 'react';
import { getTitleLogo, type TitleLogo } from '../api/endpoints.js';
import { logoTone, type LogoTone } from './logo-tone.js';

/**
 * Логотипы тайтлов по id — один запрос на тайтл за всю жизнь вкладки.
 *
 * Фокус на главной гуляет по одним и тем же карточкам, и каждый возврат к уже
 * виденной не должен снова идти в сеть. Хранится обещание, а не ответ: два
 * быстрых наведения подряд ждут один и тот же запрос, а не запускают два.
 * Ошибка — то же, что «логотипа нет»: название остаётся текстом.
 */
const logos = new Map<string, Promise<TitleLogo | null>>();

/**
 * Уже полученные ответы — в том числе «логотипа нет» (`null`).
 *
 * Обещание отвечает не раньше следующей микрозадачи, и без этой таблицы даже
 * известный логотип на первой отрисовке тайтла считался бы «ещё едет»: блок
 * названия вставал бы сперва в одну высоту, а кадром позже — в другую.
 */
const answers = new Map<string, TitleLogo | null>();

export function loadTitleLogo(contentId: string): Promise<TitleLogo | null> {
  const known = logos.get(contentId);
  if (known) return known;
  const pending = getTitleLogo(contentId)
    .then((response) => response.logo ?? null)
    .catch(() => null)
    .then((logo) => {
      answers.set(contentId, logo);
      return logo;
    });
  logos.set(contentId, pending);
  return pending;
}

/** Ответ о логотипе, если он уже получен; `undefined` — ещё нет. */
export function knownTitleLogo(contentId: string): TitleLogo | null | undefined {
  return answers.get(contentId);
}

/**
 * Логотип тайтла: из готовой карточки (`known`), иначе — лёгким запросом.
 * `undefined` — ещё не знаем, `null` — логотипа нет.
 */
export function useTitleLogo(contentId: string | null, known?: TitleLogo | null): TitleLogo | null | undefined {
  const [state, setState] = useState<{ id: string | null; logo: TitleLogo | null | undefined }>({
    id: contentId,
    logo: known,
  });
  useEffect(() => {
    if (contentId === null) return undefined;
    if (known !== undefined) {
      setState({ id: contentId, logo: known });
      return undefined;
    }
    let alive = true;
    void loadTitleLogo(contentId).then((logo) => {
      if (alive) setState({ id: contentId, logo });
    });
    return () => {
      alive = false;
    };
  }, [contentId, known]);
  if (state.id === contentId && state.logo !== undefined) return state.logo;
  // Ответ уже получен раньше (фокус возвращался, автопоказ готовил заранее) —
  // он известен с первой же отрисовки, без кадра «ещё едет».
  return contentId === null ? undefined : (known ?? knownTitleLogo(contentId));
}

/**
 * Ступени размера текстового названия: длинное имя мельче, чтобы в две
 * строки влезало целиком, а не обрывалось многоточием на втором слове.
 */
function lengthStep(title: string): 's' | 'm' | 'l' | 'xl' {
  const length = title.length;
  if (length <= 12) return 's';
  if (length <= 22) return 'm';
  if (length <= 34) return 'l';
  return 'xl';
}

/**
 * Сколько название ждёт свой логотип, прежде чем встать текстом.
 *
 * Обычно ответ о логотипе и сама картинка приходят за десятки миллисекунд (а у
 * автопоказа — заранее), и ожидания зритель не видит. Две секунды — предел для
 * медленной сети: дольше держать верх без названия нельзя. Срок один на показ
 * тайтла и считается с его появления, а не с каждого шага загрузки.
 */
export const TITLE_LOGO_WAIT_MS = 2_000;

/**
 * Логотип тайтла и всё, что нужно для его показа, — заранее.
 *
 * Для тайтла, который вот-вот встанет наверх сам (автопоказ): к моменту смены
 * известен и ответ «есть ли логотип», и тон картинки, а сама картинка лежит в
 * кэше браузера — название проявляется вместе с остальным верхом, сразу в
 * окончательном виде.
 */
export function preloadTitleLogo(contentId: string): void {
  void loadTitleLogo(contentId).then((logo) => {
    // Тон считается по самой картинке — этот же запрос её и загружает.
    if (logo !== null) void logoTone(logo.url);
  });
}

/** Пропорции логотипа, пока сервер их не сообщил и картинка не загрузилась. */
const DEFAULT_LOGO_RATIO = 3;

/** Ширина к высоте из ответа сервера; `null` — размеров в ответе нет. */
function knownRatio(logo: TitleLogo | null | undefined): number | null {
  if (!logo?.width || !logo.height) return null;
  return logo.width / logo.height;
}

/**
 * Крупное название тайтла: фирменный логотип, а если его нет — текст шрифтом
 * заголовков капсом.
 *
 * Коробка названия — ровно по содержимому, без запаса «на всякий случай».
 * У логотипа размеры известны из ответа заранее (`width`/`height`), и коробка
 * сразу встаёт по его пропорциям, вписанным в предельные ширину и высоту:
 * широкий логотип выходит низким, компактный — высоким, и под ним не остаётся
 * пустой полосы до строки с годом. Картинка проявляется в уже готовой
 * коробке — ничего ниже не сдвигается. Пока неизвестно, есть ли логотип
 * вовсе, на месте названия стоит невидимый текст той же высоты.
 *
 * Цветной логотип — всегда в родных цветах. Белым силуэтом — только почти
 * чёрный и почти бесцветный (его на тёмном кадре не видно вовсе), а умеренно
 * тёмному цветному — мягкая светлая подсветка позади (`logo-tone.ts`).
 * Картинка проявляется, когда известен и её тон: иначе тёмный логотип успел
 * бы мелькнуть чёрным, прежде чем стать белым.
 *
 * Подмены «текст → логотип» у зрителя на глазах не бывает (правило владельца).
 * Пока неизвестно, чем станет название, оно не показывается вовсе — место под
 * него стоит. Решение принимается один раз на показ: логотип готов — проявляется
 * логотип; логотипа нет, картинка не пришла или это плашка — проявляется текст.
 * Не успело решиться за `TITLE_LOGO_WAIT_MS` — тоже текст, и опоздавший логотип
 * его уже не сменит: он покажется при следующем показе этого тайтла, из кэша.
 */
export function TitleHeading({
  title,
  logo,
  level = 1,
  className,
}: {
  title: string;
  /** `undefined` — логотип ещё едет, `null` — его нет. */
  logo: TitleLogo | null | undefined;
  level?: 1 | 2;
  className?: string;
}): React.JSX.Element {
  const [ready, setReady] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [measured, setMeasured] = useState<{ url: string; ratio: number } | null>(null);
  const [tone, setTone] = useState<{ url: string; tone: LogoTone } | null>(null);
  /** Название, у которого срок ожидания логотипа вышел: оно остаётся текстом. */
  const [textFor, setTextFor] = useState<string | null>(null);
  const logoShown = useRef(false);
  const url = logo?.url ?? null;

  useEffect(() => {
    // Срок — от появления названия; приход ответа или картинки его не продлевает.
    const timer = window.setTimeout(() => {
      // Логотип уже на экране — он и остаётся; иначе название закрепляется текстом.
      if (!logoShown.current) setTextFor(title);
    }, TITLE_LOGO_WAIT_MS);
    return () => window.clearTimeout(timer);
  }, [title]);

  useEffect(() => {
    if (url === null) return undefined;
    let alive = true;
    void logoTone(url).then((value) => {
      if (alive) setTone({ url, tone: value });
    });
    return () => {
      alive = false;
    };
  }, [url]);

  const shade = tone?.url === url ? tone.tone : null;
  // Плашка без прозрачного поля — не логотип (см. `LogoTone`): название текстом.
  const timedOut = textFor === title;
  const useLogo = url !== null && failed !== url && shade !== 'boxed' && !timedOut;
  const ratio = knownRatio(logo) ?? (measured?.url === url ? measured.ratio : DEFAULT_LOGO_RATIO);
  const shown = useLogo && ready === url && shade !== null;
  useEffect(() => {
    logoShown.current = shown;
  }, [shown]);
  const Tag = level === 1 ? 'h1' : 'h2';
  const classes = [
    'title-heading',
    useLogo ? 'is-logo' : '',
    shown ? 'has-logo' : '',
    shown && shade === 'glow' ? 'has-glow' : '',
    logo === undefined && !timedOut ? 'is-pending' : '',
    className ?? '',
  ];
  return (
    <Tag
      className={classes.filter(Boolean).join(' ')}
      data-length={lengthStep(title)}
      style={useLogo ? ({ '--logo-ratio': ratio.toFixed(4) } as React.CSSProperties) : undefined}
    >
      <span className="title-heading-text">{title}</span>
      {useLogo && url !== null && (
        <img
          key={url}
          className={shade === null || shade === 'plain' ? 'title-heading-logo' : `title-heading-logo is-${shade}`}
          src={url}
          alt=""
          decoding="async"
          onLoad={(event) => {
            const image = event.currentTarget;
            if (knownRatio(logo) === null && image.naturalWidth > 0 && image.naturalHeight > 0) {
              setMeasured({ url, ratio: image.naturalWidth / image.naturalHeight });
            }
            setReady(url);
          }}
          // Картинка не пришла — остаёмся с текстом, без битой иконки.
          onError={() => setFailed(url)}
        />
      )}
    </Tag>
  );
}
